import { useEffect, useRef, useState } from "react";
import { X, Play, Pause, RotateCcw } from "lucide-react";

export function HoldTimer({ exercise, onClose }) {
  const total = exercise.hold;
  const [remaining, setRemaining] = useState(total);
  const [running, setRunning] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (running) {
      intervalRef.current = setInterval(() => {
        setRemaining((r) => {
          if (r <= 1) {
            clearInterval(intervalRef.current);
            setRunning(false);
            try {
              if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
            } catch (e) {}
            return 0;
          }
          return r - 1;
        });
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [running]);

  const reset = () => {
    setRunning(false);
    clearInterval(intervalRef.current);
    setRemaining(total);
  };

  const R = 130;
  const C = 2 * Math.PI * R;
  const pct = total ? remaining / total : 0;
  const offset = C * (1 - pct);
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");

  return (
    <div
      data-testid="hold-timer-overlay"
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-[#0a0a0a]/97 backdrop-blur-md px-6 animate-slideup"
    >
      <button
        data-testid="hold-timer-close"
        onClick={onClose}
        className="absolute top-6 right-6 flex h-12 w-12 items-center justify-center border border-zinc-800 text-zinc-400 active:scale-90"
      >
        <X size={22} />
      </button>

      <p className="font-head text-2xl uppercase tracking-tight text-zinc-400">Tenuta</p>
      <h2 className="font-head text-4xl uppercase tracking-tight text-white text-center mb-8 max-w-xs">
        {exercise.name}
      </h2>

      <div className="relative" style={{ width: 300, height: 300 }}>
        <svg width="300" height="300" className="-rotate-90">
          <circle cx="150" cy="150" r={R} stroke="#27272a" strokeWidth="14" fill="none" />
          <circle
            cx="150"
            cy="150"
            r={R}
            stroke="#fa003f"
            strokeWidth="14"
            fill="none"
            strokeLinecap="butt"
            strokeDasharray={C}
            strokeDashoffset={offset}
            style={{ transition: "stroke-dashoffset 1s linear", filter: "drop-shadow(0 0 8px rgba(250,0,63,0.5))" }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span data-testid="hold-timer-display" className="font-head text-7xl font-black tracking-tighter text-white tabular-nums">
            {total >= 60 ? `${mm}:${ss}` : remaining}
          </span>
        </div>
      </div>

      <div className="mt-10 flex items-center gap-4 w-full max-w-sm">
        <button
          data-testid="hold-timer-reset"
          onClick={reset}
          className="flex h-16 w-16 items-center justify-center border border-zinc-800 text-zinc-300 active:scale-90"
        >
          <RotateCcw size={24} />
        </button>
        <button
          data-testid="hold-timer-toggle"
          onClick={() => (remaining === 0 ? reset() : setRunning((r) => !r))}
          className="flex h-16 flex-1 items-center justify-center gap-2 bg-[#fa003f] font-head text-2xl font-bold uppercase tracking-wide text-white active:scale-[0.98] transition-transform"
        >
          {running ? (
            <>
              <Pause size={24} /> Pausa
            </>
          ) : (
            <>
              <Play size={24} /> {remaining === total ? "Avvia" : "Riprendi"}
            </>
          )}
        </button>
      </div>
    </div>
  );
}
